import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, fmt, fmtDate, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, RefreshCw, TrendingDown, TrendingUp, Wallet, Package, AlertTriangle, ChevronDown, ChevronUp, Edit3, Save, X, FileDown } from 'lucide-react';

function fmtINR(n) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n || 0);
}

export default function CustomerLedger() {
  const { customerId } = useParams();
  const navigate       = useNavigate();
  const { auth }       = useAuth();
  const isAdmin        = auth?.user?.role === 'admin';

  const [ledger,   setLedger]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editOpening, setEditOpening]   = useState(false);
  const [openingDraft, setOpeningDraft] = useState('');
  const [savingOpening, setSavingOpening] = useState(false);

  const downloadPDF = () => {
    const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
    const stored = localStorage.getItem("lpg_company_auth");
    const token = stored ? JSON.parse(stored)?.token : null;
    if (!token) return;
    const url = `${API_BASE}/customers/${customerId}/ledger/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `ledger_${ledger?.customer?.name || "party"}.pdf`;
        a.click();
      })
      .catch(() => {});
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/customers/${customerId}/ledger`);
      setLedger(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const saveOpening = async () => {
    setSavingOpening(true);
    try {
      await api.patch(`/customers/${customerId}/opening-balance`, { opening_balance: parseFloat(openingDraft) || 0 });
      setEditOpening(false);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSavingOpening(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-6">
        <button onClick={() => navigate('/parties')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4 transition-colors">
          <ArrowLeft size={14} /> Back to Customers
        </button>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  const { customer, entries, total_billed, total_paid, outstanding, cylinder_balance, total_cyl_pending, pending_challans } = ledger;

  return (
    <div className="px-6 py-6 max-w-5xl" data-testid="customer-ledger-page">
      {/* Back */}
      <button onClick={() => navigate('/parties')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-5 transition-colors" data-testid="back-to-customers">
        <ArrowLeft size={14} /> Back to Customers
      </button>
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">{customer.customer_type}</div>
          <h1 className="font-black text-3xl tracking-tight">{customer.name}</h1>
          <div className="text-sm text-gray-500 mt-1">{customer.phone || '—'} · {customer.address || '—'}</div>          {/* Opening balance */}
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
            <Wallet size={13} className="text-gray-400" />
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Opening</span>
            {!editOpening ? (
              <>
                <span className="font-bold text-sm text-gray-800" data-testid="opening-balance-display">
                  {fmtINR(customer.opening_balance || 0)}
                </span>
                {isAdmin && (
                  <button onClick={() => { setOpeningDraft(String(customer.opening_balance || 0)); setEditOpening(true); }}
                    className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1 ml-1" data-testid="edit-opening-btn">
                    <Edit3 size={11} /> Edit
                  </button>
                )}
              </>
            ) : (
              <>
                <input type="number" step="0.01" value={openingDraft} onChange={e => setOpeningDraft(e.target.value)}
                  className="h-7 px-2 border border-gray-300 rounded w-28 text-right text-sm"
                  autoFocus data-testid="opening-balance-input" />
                <button onClick={saveOpening} disabled={savingOpening}
                  className="h-7 px-2 bg-gray-900 text-white rounded text-xs font-bold flex items-center gap-1 disabled:opacity-50" data-testid="save-opening-btn">
                  <Save size={11} /> {savingOpening ? '…' : 'Save'}
                </button>
                <button onClick={() => setEditOpening(false)} className="text-xs text-gray-400 hover:text-gray-700"><X size={13} /></button>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-400 mb-1">Outstanding</div>
          <div className={`font-black text-3xl ${outstanding > 0 ? 'text-amber-700' : outstanding < 0 ? 'text-green-700' : 'text-gray-400'}`}
            data-testid="ledger-closing-balance">
            {outstanding === 0 ? 'Settled' : fmtINR(Math.abs(outstanding))}
          </div>
          {outstanding !== 0 && (
            <div className="text-xs text-gray-500 mt-0.5">
              {outstanding > 0 ? 'Customer owes you' : 'You owe customer'}
            </div>
          )}
          <button onClick={downloadPDF}
            className="mt-3 flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-semibold"
            data-testid="download-ledger-pdf">
            <FileDown size={14} /> Download PDF
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-xl font-black text-gray-900">{fmtINR(total_billed)}</div>
          <div className="text-xs text-gray-400 mt-1">Total Billed</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-xl font-black text-green-700">{fmtINR(total_paid)}</div>
          <div className="text-xs text-gray-400 mt-1">Total Received</div>
        </div>
        <div className={`border rounded-xl p-4 text-center ${total_cyl_pending > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
          <div className={`text-xl font-black ${total_cyl_pending > 0 ? 'text-amber-700' : 'text-gray-900'}`}>{total_cyl_pending}</div>
          <div className="text-xs text-gray-400 mt-1">Pending Cylinders</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-xl font-black text-gray-700">{pending_challans?.length || 0}</div>
          <div className="text-xs text-gray-400 mt-1">Pending Challans</div>
        </div>
      </div>

      {/* Cylinder balance */}
      {cylinder_balance && cylinder_balance.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-5" data-testid="cylinder-balance-panel">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="font-bold text-sm text-gray-800 flex items-center gap-2">
              <Package size={14} /> Cylinder Balance (Filled Given − MT Received)
            </div>
          </div>
          <div className="flex flex-wrap gap-3 p-4">
            {cylinder_balance.map((c, i) => (
              <div key={i} className={`border rounded-xl p-3 min-w-[110px] text-center ${c.pending > 0 ? 'bg-amber-50 border-amber-200' : c.pending < 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`text-2xl font-black ${c.pending > 0 ? 'text-amber-700' : c.pending < 0 ? 'text-blue-700' : 'text-gray-500'}`}>
                  {c.pending > 0 ? `+${c.pending}` : c.pending}
                </div>
                <div className="text-xs font-semibold text-gray-700 mt-0.5">{c.brand} {c.size}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {c.filled_given}↓ {c.mt_received}↑
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending challans */}
      {pending_challans && pending_challans.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl overflow-hidden mb-5" data-testid="pending-challans-panel">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
            <div className="font-bold text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle size={14} /> Pending Challans ({pending_challans.length})
            </div>
            <div className="text-xs text-amber-700 font-semibold">
              Total pending: {fmtINR(pending_challans.reduce((s, c) => s + c.remaining, 0))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-amber-50/50 border-b border-amber-100">
                  {['Date','Challan #','Sale Amount','Remaining'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pending_challans.map((c, i) => (
                  <tr key={i} className="border-b border-amber-50">
                    <td className="px-4 py-2.5 text-gray-600">{fmtDate(c.date)}</td>
                    <td className="px-4 py-2.5 font-mono font-semibold text-gray-800">{c.challan_no || '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-gray-700">{fmtINR(c.amount)}</td>
                    <td className="px-4 py-2.5 font-mono font-bold text-amber-700">{fmtINR(c.remaining)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transaction table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Transaction Timeline</span>
          <span className="text-xs text-gray-400">{entries.length} entries · latest first</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ledger-entries-table">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="w-6 px-2 py-3"></th>
                {['Date','Challan','Description','Debit ₹','Credit ₹','Balance ₹'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-gray-400 text-sm">No transactions yet.</td>
                </tr>
              ) : entries.map((e, i) => (
                <React.Fragment key={e.id || i}>
                  <tr className={`border-b border-gray-50 transition-colors hover:bg-gray-50/50 ${e.type === 'sale' ? '' : ''}`}>
                    <td className="px-2 py-3">
                      {e.type === 'sale' && (
                        <button onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                          className="text-gray-300 hover:text-gray-600 w-4 h-4 flex items-center justify-center">
                          {expanded === e.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="px-4 py-3">
                      {e.ref
                        ? <span className="text-xs font-semibold text-gray-700 font-mono">{e.ref}</span>
                        : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${e.type === 'sale' ? 'bg-red-400' : 'bg-green-500'}`} />
                        <span className="text-xs text-gray-700">{e.description}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {e.debit > 0
                        ? <span className="text-sm font-semibold text-red-700">{fmtINR(e.debit)}</span>
                        : <span className="text-xs text-gray-200">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {e.credit > 0
                        ? <span className="text-sm font-semibold text-green-700">{fmtINR(e.credit)}</span>
                        : <span className="text-xs text-gray-200">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-bold ${e.balance > 0 ? 'text-amber-700' : e.balance < 0 ? 'text-green-700' : 'text-gray-400'}`}>
                        {fmtINR(Math.abs(e.balance))}
                        {e.balance < 0 && <span className="text-[10px] ml-0.5 font-normal">(Cr)</span>}
                      </span>
                    </td>
                  </tr>
                  {expanded === e.id && e.type === 'sale' && (
                    <tr className="bg-gray-50">
                      <td colSpan={7} className="px-6 py-3">
                        <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                          {e.godown && <span><span className="text-gray-400">Godown:</span> {e.godown}</span>}
                          {e.vehicle && <span><span className="text-gray-400">Vehicle:</span> {e.vehicle}</span>}
                          <span><span className="text-gray-400">Cylinders:</span> {e.filled} filled, {e.mt} MT</span>
                        </div>
                        {e.items && e.items.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {e.items.map((it, j) => (
                              <span key={j} className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs font-mono">
                                {it.brand} {it.size} · F:{it.filled} MT:{it.mt} · ₹{it.rate_per_kg}/kg
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {entries.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td colSpan={4} className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">Totals</td>
                  <td className="px-4 py-3 text-right font-bold text-red-700 text-sm">{fmtINR(total_billed)}</td>
                  <td className="px-4 py-3 text-right font-bold text-green-700 text-sm">{fmtINR(total_paid)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-black text-sm ${outstanding > 0 ? 'text-amber-700' : outstanding < 0 ? 'text-green-700' : 'text-gray-500'}`}>
                      {fmtINR(Math.abs(outstanding))}
                      {outstanding < 0 && <span className="text-[10px] ml-0.5 font-normal">(Cr)</span>}
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
